import React, { useState, useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import { EMPTY_THEME_FORM, type ThemeFormValues } from '@/components/DesignSystem/ThemeEditorPanel';
import { ComponentShowcase } from '@/components/DesignSystem/ComponentShowcase';
import { type ThemePreset } from '@/components/DesignSystem/ThemePresetCard';
import { ThemePresetPicker } from '@/components/DesignSystem/ThemePresetPicker';
import { loadPresetIntoForm } from '@/components/DesignSystem/loadPresetIntoForm';
import { CreatePresetModal } from '@/components/DesignSystem/CreatePresetModal';
import { useThemePreview } from '@/hooks/useThemePreview';
import { Button } from '@/components/ui/Button/Button';
import { Dialog } from '@/components/ui/Modal/Modal';
import type { ResolvedTheme } from '@/lib/orgTheme';
import { Plus } from 'lucide-react';
import { setLocaleSettings } from '@/lib/localeFormatters';
import { useLocaleStore } from '@/stores/localeStore';

/**
 * Super Admin Design System page.
 * Full-width component library with inline theme editing controls.
 * Sticky save bar at the top for quick save/reset.
 */
export function DesignSystem() {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<ThemeFormValues>(EMPTY_THEME_FORM);
  const [saved, setSaved] = useState(false);
  const [activePresetId, setActivePresetId] = useState<string | null>('default');

  // Modal state
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [duplicatingPreset, setDuplicatingPreset] = useState<ThemePreset | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  // ─── Fetch presets ─────────────────────────────────────────────────
  const { data: presets = [] } = useQuery({
    queryKey: ['theme-presets'],
    queryFn: async () => {
      const res = await apiFetch('/api/admin/theme-presets');
      if (!res.ok) return [];
      const json = await res.json();
      return (json.data || []) as ThemePreset[];
    },
  });

  // Fetch current platform theme (default preset)
  const { data: platformTheme } = useQuery({
    queryKey: ['platform-theme'],
    queryFn: async () => {
      const res = await apiFetch('/api/admin/platform-theme');
      if (!res.ok) return null;
      const json = await res.json();
      return json.data as Record<string, any> | null;
    },
  });

  // Populate form when platform theme loads (initial)
  useEffect(() => {
    if (platformTheme && activePresetId === 'default') {
      populateFormFromTheme(platformTheme);
    }
  }, [platformTheme]);

  // ─── Live preview ──────────────────────────────────────────────────
  const previewTheme: ResolvedTheme = {
    primaryColor: form.primaryColor || null,
    secondaryColor: form.secondaryColor || null,
    accentColor: form.accentColor || null,
    successColor: form.successColor || null,
    warningColor: form.warningColor || null,
    errorColor: form.errorColor || null,
    infoColor: form.infoColor || null,
    grayColor: form.grayColor || null,
    headingFontFamily: form.headingFontFamily || null,
    bodyFontFamily: form.bodyFontFamily || null,
    headingFontSize: form.headingFontSize || null,
    bodyFontSize: form.bodyFontSize || null,
    headingFontUrl: null,
    bodyFontUrl: null,
    logoUrl: form.logoUrl || null,
    logoMarkUrl: form.logoMarkUrl || null,
    faviconUrl: form.faviconUrl || null,
    borderRadius: form.borderRadius || null,
    customCss: form.customCss || null,
    darkModeEnabled: form.darkModeEnabled,
  };
  useThemePreview(previewTheme);

  // ─── Save mutation (updates the active preset) ─────────────────────
  const saveMutation = useMutation({
    mutationFn: async (data: ThemeFormValues) => {
      const body = formToPayload(data);

      if (activePresetId && activePresetId !== 'default') {
        const res = await apiFetch(`/api/admin/theme-presets/${activePresetId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err?.error?.message || 'Failed to save preset');
        }
        return res.json();
      }

      const res = await apiFetch('/api/admin/platform-theme', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.error?.message || 'Failed to save platform theme');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['platform-theme'] });
      queryClient.invalidateQueries({ queryKey: ['theme-presets'] });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    },
  });

  // ─── Reset mutation ────────────────────────────────────────────────
  // Restores (or recreates) the default theme with the canonical seed values,
  // then loads it into the editor.
  const resetMutation = useMutation({
    mutationFn: async () => {
      const res = await apiFetch('/api/admin/platform-theme', { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed to reset');
      const json = await res.json();
      return json.data as Record<string, any>;
    },
    onSuccess: (data) => {
      setActivePresetId('default');
      populateFormFromTheme(data);
      queryClient.invalidateQueries({ queryKey: ['platform-theme'] });
      queryClient.invalidateQueries({ queryKey: ['theme-presets'] });
    },
  });

  // ─── Create preset mutation ────────────────────────────────────────
  const createMutation = useMutation({
    mutationFn: async ({ name, sourcePreset }: { name: string; sourcePreset?: ThemePreset }) => {
      const body = sourcePreset
        ? { ...presetToPayload(sourcePreset), name }
        : { ...formToPayload(form), name };

      const res = await apiFetch('/api/admin/theme-presets', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.error?.message || 'Failed to create preset');
      }
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['theme-presets'] });
      setCreateModalOpen(false);
      setDuplicatingPreset(null);
      if (data?.data?.id) {
        loadPreset(data.data);
      }
    },
  });

  // ─── Delete preset mutation ────────────────────────────────────────
  const deleteMutation = useMutation({
    mutationFn: async (presetId: string) => {
      const res = await apiFetch(`/api/admin/theme-presets/${presetId}`, { method: 'DELETE' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.error?.message || 'Failed to delete preset');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['theme-presets'] });
      if (deleteConfirmId === activePresetId) {
        setActivePresetId('default');
        if (platformTheme) populateFormFromTheme(platformTheme);
      }
      setDeleteConfirmId(null);
    },
  });

  // ─── Helpers ───────────────────────────────────────────────────────

  function populateFormFromTheme(theme: Record<string, any>) {
    setForm(loadPresetIntoForm(theme));
  }

  function loadPreset(preset: ThemePreset | Record<string, any>) {
    setActivePresetId(preset.id);
    populateFormFromTheme(preset);
  }

  const updateLocaleStore = useLocaleStore(s => s.updateSettings);

  // Locale-related keys that need to sync to the reactive locale store
  const LOCALE_KEYS = new Set(['dateFormat', 'timeFormat', 'thousandsSeparator', 'decimalSeparator', 'currencySymbol', 'currencyFormat']);

  const updateField = useCallback(<K extends keyof ThemeFormValues>(key: K, value: ThemeFormValues[K]) => {
    setForm(prev => ({ ...prev, [key]: value }));

    // Sync locale-related fields to the reactive locale store so date/time components update live
    if (LOCALE_KEYS.has(key) && typeof value === 'string' && value !== '') {
      const partial = { [key]: value };
      setLocaleSettings(partial);
      updateLocaleStore(partial);
    }
  }, [updateLocaleStore]);

  function handleDuplicatePreset(preset: ThemePreset) {
    setDuplicatingPreset(preset);
    setCreateModalOpen(true);
  }

  function handleCreateConfirm(name: string) {
    createMutation.mutate({ name, sourcePreset: duplicatingPreset || undefined });
  }

  const activePresetName = presets.find(p => p.id === activePresetId)?.name || 'Default';

  return (
    <div data-no-translate>
      {/* Sticky save bar */}
      <div className="sticky top-0 z-20 bg-background border-b border-border shadow-sm px-6 py-3">
        <div className="max-w-[1400px] mx-auto flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold text-foreground">
              Design System
              {activePresetId && (
                <span className="ml-2 text-xs font-normal text-muted-foreground">
                  — editing: {activePresetName}
                </span>
              )}
            </h2>
            <p className="text-xs text-muted-foreground">
              Configure the platform's visual identity — colors, typography, and layout. Organizations can override these defaults.
            </p>
          </div>
          <div className="flex items-center gap-3">
            {saved && (
              <span className="text-sm text-success-600">Saved</span>
            )}
            {saveMutation.error && (
              <span className="text-sm text-error-600">{(saveMutation.error as Error).message}</span>
            )}
            <Button type="button" variant="outline" size="sm" onClick={() => resetMutation.mutate()} loading={resetMutation.isPending}>
              Reset
            </Button>
            <Button type="button" variant="primary" size="sm" onClick={() => saveMutation.mutate(form)} loading={saveMutation.isPending}>
              Save Changes
            </Button>
          </div>
        </div>
      </div>

      {/* Theme Presets Section */}
      <ThemePresetPicker
        presets={presets}
        activePresetId={activePresetId}
        onPick={loadPreset}
        onDelete={(id) => setDeleteConfirmId(id)}
        onDuplicate={handleDuplicatePreset}
        headerAction={
          <Button
            type="button"
            variant="outline"
            size="sm"
            leftIcon={<Plus size={14} />}
            onClick={() => { setDuplicatingPreset(null); setCreateModalOpen(true); }}
          >
            New Preset
          </Button>
        }
      />

      {/* Full-width Component Showcase with inline editors */}
      <ComponentShowcase
        hideNav
        form={form}
        onChange={updateField}
      />

      {/* Create / Duplicate Modal */}
      <CreatePresetModal
        isOpen={createModalOpen}
        onClose={() => { setCreateModalOpen(false); setDuplicatingPreset(null); }}
        onConfirm={handleCreateConfirm}
        loading={createMutation.isPending}
        duplicatingName={duplicatingPreset?.name}
      />

      {/* Delete Confirm Dialog */}
      {deleteConfirmId && (
        <Dialog
          isOpen
          onClose={() => setDeleteConfirmId(null)}
          title="Delete Preset"
          onConfirm={() => deleteMutation.mutate(deleteConfirmId)}
          confirmText="Delete"
          confirmVariant="danger"
          isLoading={deleteMutation.isPending}
        >
          <p className="text-sm text-muted-foreground">
            Are you sure you want to delete this preset? This action cannot be undone.
          </p>
        </Dialog>
      )}
    </div>
  );
}

// ─── Utility: form values → API payload ────────────────────────────

function formToPayload(data: ThemeFormValues): Record<string, any> {
  const body: Record<string, any> = {};
  const omitIfEmpty = new Set([
    'dateFormat', 'timeFormat', 'currencyFormat',
    'thousandsSeparator', 'decimalSeparator', 'currencySymbol',
  ]);
  for (const [key, value] of Object.entries(data)) {
    if (key === 'darkModeEnabled' || key === 'showCalendarWeeks') {
      body[key] = value;
    } else if (key === 'weekStart') {
      if (value !== '' && value !== null && value !== undefined) {
        body[key] = Number(value);
      }
    } else if (omitIfEmpty.has(key)) {
      if (value !== '' && value !== null && value !== undefined) {
        body[key] = value;
      }
    } else {
      body[key] = value || null;
    }
  }
  return body;
}

function presetToPayload(preset: ThemePreset): Record<string, any> {
  const p = preset as Record<string, any>;
  return {
    primaryColor: p.primaryColor,
    secondaryColor: p.secondaryColor,
    accentColor: p.accentColor,
    successColor: p.successColor,
    warningColor: p.warningColor,
    errorColor: p.errorColor,
    infoColor: p.infoColor,
    grayColor: p.grayColor,
    headingFontFamily: p.headingFontFamily,
    bodyFontFamily: p.bodyFontFamily,
    headingFontSize: p.headingFontSize,
    bodyFontSize: p.bodyFontSize,
    logoUrl: p.logoUrl,
    logoMarkUrl: p.logoMarkUrl,
    faviconUrl: p.faviconUrl,
    borderRadius: p.borderRadius,
    customCss: p.customCss,
    darkModeEnabled: p.darkModeEnabled,
    dateFormat: p.dateFormat,
    timeFormat: p.timeFormat,
    weekStart: p.weekStart,
    showCalendarWeeks: p.showCalendarWeeks,
    thousandsSeparator: p.thousandsSeparator,
    decimalSeparator: p.decimalSeparator,
    currencySymbol: p.currencySymbol,
    currencyFormat: p.currencyFormat,
  };
}
